<script lang="ts">
	//Hooks
	import { createEventDispatcher } from 'svelte';
	//Formating
	import { format } from 'svelte-i18n';
	// Design system components
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import {
		Text,
		Modal,
		Button,
		Container,
		Wizard,
		WizardItem,
		WizardContainer,
		WizardContent,
		CardGrid,
		SelectCard,
		Alert,
	} from '@Shelly/shelly-design-system';
	//Components
	import DayPicker from '../../TimePicker/DayPicker.svelte';
	import DoubleTimePicker from '../../TimePicker/DoubleTimePicker.svelte';
	//Types
	import type { PowerPricesData, PowerPricesInterval } from '../../../types/PowerPrices';
	import { addDate, dateFormat } from '../../../helpers/date_helper';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let editedInterval: PowerPricesInterval,
		powerPrices: PowerPricesData,
		currency = '',
		showModal = false,
		waitForResponse = false;

	let errorDays = {
		show: false,
		label: $format('alert_danger_sched_week_days_error', {
			default: 'Select at least one day!',
		}),
	};

	let windowClientWidth: number;

	const dispatch = createEventDispatcher(),
		save = async () => {
			errorDays.show = false;
			if (editedInterval.days.length == 0) {
				errorDays.show = true;
				return;
			}
			editedInterval.tariffID = selectedTariff;
			dispatch('finish');
		},
		roundTime = (time: string, currentTime: string) => {
			const split = time.split(':'),
				splitCurrentTime = currentTime.split(':');
			if (split[1] != splitCurrentTime[1] && !/^(00|30)$/.test(split[1])) {
				const date = new Date().setHours(
					Number(splitCurrentTime[0]),
					Number(splitCurrentTime[1]),
					0,
					0,
				);
				if (/^(29|59|01|31)$/.test(split[1])) {
					const inc = /^(29|59)$/.test(split[1]) ? -1 : 1;
					return dateFormat(addDate(date, 'i', 30 * inc), 'H:i');
				}
				return dateFormat(date, 'H:i');
			}
			return null;
		},
		onClose = () => {
			showModal = false;
		},
		handleTimeChange = (time: string) => {
			const stepTimeFrom = roundTime(time[0], editedInterval.from);
			if (stepTimeFrom) {
				editedInterval.from = stepTimeFrom;
				intervalTime = [editedInterval.from, editedInterval.to];
			} else {
				editedInterval.from = time[0];
				const stepTimeTo = roundTime(time[1], editedInterval.to);
				if (stepTimeTo) {
					editedInterval.to = stepTimeTo;
					intervalTime = [editedInterval.from, editedInterval.to];
				} else {
					editedInterval.to = time[1];
				}
			}
		},
		onChangeDays = (e) => {
			editedInterval.days = e.detail.map((i: number) => (i = i == 6 ? 0 : i + 1));
			errorDays.show = editedInterval.days.length == 0;
		},
		days = editedInterval.days.map((i: number) => (i = i == 0 ? 6 : i - 1)),
		onSelectTariff = (id: number) => {
			selectedTariff = id;
		};

	let intervalTime = [editedInterval.from, editedInterval.to],
		selectedTariff = editedInterval.tariffID || Object.values(powerPrices.tariffs)[0].id;

	$: isMobile = windowClientWidth < SystemScreenBreakpoints.MOBILE_MAX;
</script>

<svelte:window bind:innerWidth={windowClientWidth} />

<Modal
	on:close={onClose}
	{showModal}
	title={editedInterval?.edit
		? $format('label_edit_interval', { default: 'Edit Interval' })
		: $format('label_add_interval', { default: 'Add New Interval' })}
	removeScrollPadding
>
	<svelte:fragment slot="content">
		<div>
			<Container class="pb-0" padding="scrollPaddings">
				<Text styleType="h2" class="mb-s">
					{$format('choose_type', {
						default: 'Choose {type}',
						values: { type: $format('title_tariff') },
					})}
				</Text>
				<CardGrid gridGap="s" sm={250} md={190} xl={190} class="mb-md-l mb-sm-m" noPaddingBottom>
					{#each Object.values(powerPrices.tariffs) as tariff}
						{@const icon = tariff?.bgColor
							? `text-${tariff.bgColor} fa-thin fa-coins`
							: 'text-color-green fa-thin fa-coins'}
						<SelectCard
							cardBgColor={$showNewLayout ? 'color-1' : undefined}
							type={isMobile ? 'horizontal' : 'vertical'}
							title={tariff.name}
							descriptiveText={`${tariff?.price} ${currency} / kWh`}
							variant="radio"
							selected={selectedTariff == tariff.id}
							{icon}
							class="width-full"
							on:click={() => {
								onSelectTariff(tariff.id);
							}}
						/>
					{/each}
				</CardGrid>
				<hr class="mt-l mb-l" />
			</Container>
			<Container padding="scrollPaddings">
				<Text element="h2" class="mb-s">
					{$format('choose_day_of_week', {
						default: 'Choose day(s) of the week',
					})}
				</Text>
				<DayPicker {days} on:change={onChangeDays} />
				{#if errorDays.show}
					<Alert class="mt-m" heading={errorDays.label} type="danger" />
				{/if}
				<hr class="mt-l mb-l" />
			</Container>
			<Container padding="scrollPaddings">
				<Text element="h2" class="mb-s text-capitalize">
					{$format('choose_time_interval', {
						default: 'Choose time interval',
					})}
				</Text>
				{#key intervalTime}
					<DoubleTimePicker
						orientation={isMobile ? 'vertical' : 'horizontal'}
						time={intervalTime}
						on:change={(e) => handleTimeChange(e.detail)}
					/>
				{/key}
			</Container>
		</div>
	</svelte:fragment>
	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{
				sm: 'm',
			}}
			variant="secondary"
			on:click={onClose}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={save}
			loading={waitForResponse}
			disabled={errorDays.show}
		>
			{$format('button_save', { default: 'Save' })}
			{$format('process_interval')}
		</Button>
	</div>
</Modal>
